import { useMemo, useState } from "react";
import { useNavigate } from "react-router";
import type { ColumnDef } from "@tanstack/react-table";
import { Activity, AppWindow, Flag, HeartPulse, MessagesSquare, Plus, RefreshCw, Search } from "lucide-react";
import { usePermission } from "@ncp/api-client";
import { PlatformIcon, tileClasses } from "@ncp/assistant-sdk";
import { formatNumber, formatRelative, useLocale, useTranslation } from "@ncp/i18n";
import { slugFromLabel } from "@ncp/forms";
import { Badge, Button, DataTable, Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, EmptyState, Field, Input, NativeSelect, PageHeader, StatCard, Tooltip, cn, notifyDone, notifySaved, toast } from "@ncp/ui";
import { useBreadcrumbs, useHelpTopic, usePaletteActions } from "../app/console-context";
import { SOLUTION_KINDS, useSolutionActions, useSolutions, type Solution, type SolutionKind } from "../features/solutions/api";

const KIND_ICON: Record<SolutionKind, string> = { WebApp: "AppWindow", Embedded: "PanelRight", Channel: "MessagesSquare", ExternalPortal: "Globe" };

/** Solutions: the registry of surfaces the platform serves, with where records open in each, who may see it and whether it is up. */
export function SolutionsPage() {
  const { t } = useTranslation();
  const { locale } = useLocale();
  const navigate = useNavigate();
  const { can } = usePermission();
  const canWrite = can("platform.workspaces.write");
  const solutions = useSolutions();
  const actions = useSolutionActions();
  const [creating, setCreating] = useState(false);
  const [q, setQ] = useState("");
  const [kind, setKind] = useState("");

  useBreadcrumbs(useMemo(() => [{ label: t("admin:nav.solutions") }], [t]));
  useHelpTopic({ title: t("admin:solutions.title"), docsAnchor: "solutions", body: (<><p>{t("admin:solutions.help.what")}</p><p>{t("admin:solutions.help.links")}</p><p>{t("admin:solutions.help.health")}</p></>) });
  const probeAll = () => actions.probeAll.mutate(undefined, {
    onSuccess: (r) => { const down = r.filter((p) => !p.ok).length; if (down) toast.warning(t("admin:solutions.probe.someDown", { count: down })); else notifyDone(t("admin:solutions.probe.allUp", { count: r.length })); },
  });
  usePaletteActions(useMemo(() => canWrite ? [
    { id: "solutions.new", label: t("admin:solutions.newSolution"), icon: Plus, permission: "platform.workspaces.write", run: () => setCreating(true) },
    { id: "solutions.probe", label: t("admin:solutions.probe.all"), icon: HeartPulse, permission: "platform.workspaces.write", run: probeAll },
  ] : [], [canWrite, t])); // eslint-disable-line react-hooks/exhaustive-deps

  const all = solutions.data ?? [];
  const rows = useMemo(() => {
    const term = q.trim().toLowerCase();
    return all.filter((s) => (!kind || s.kind === kind) && (!term || s.name.toLowerCase().includes(term) || s.key.includes(term) || (s.nameAr ?? "").includes(term)));
  }, [all, kind, q]);
  const probed = all.filter((s) => s.enabled && s.probeUrl && s.lastHealthOk != null);
  const up = probed.filter((s) => s.lastHealthOk).length;

  const columns = useMemo<ColumnDef<Solution, unknown>[]>(() => [
    {
      id: "name", header: t("admin:common.name"), accessorFn: (s) => s.name,
      cell: ({ row: { original: s } }) => (
        <div className="flex items-center gap-3">
          <span className={tileClasses("sand", "size-9")}><PlatformIcon name={s.icon} fallback={AppWindow} className="size-4" /></span>
          <div className="min-w-0">
            <div className="flex flex-wrap items-center gap-1.5">
              <span className="truncate font-medium">{locale === "ar" && s.nameAr ? s.nameAr : s.name}</span>
              {s.isBuiltIn ? <Badge variant="outline" size="sm">{t("admin:common.builtIn")}</Badge> : null}
              {s.sourcePackageKey ? <Badge variant="camel" size="sm">{s.sourcePackageKey}</Badge> : null}
              {!s.enabled ? <Badge variant="default" size="sm">{t("admin:common.disabled")}</Badge> : null}
            </div>
            <p className="truncate text-xs text-muted-foreground"><code className="font-mono" dir="ltr">{s.key}</code> · <span dir="ltr">{s.baseUrl || "—"}</span></p>
          </div>
        </div>
      ),
    },
    {
      id: "kind", header: t("admin:solutions.fields.kind"), accessorFn: (s) => s.kind,
      cell: ({ row: { original: s } }) => <Badge variant="outline" className="gap-1"><PlatformIcon name={KIND_ICON[s.kind]} className="size-3" /> {t(`admin:solutions.kind.${s.kind}`)}</Badge>,
    },
    { id: "health", header: t("admin:solutions.health.title"), accessorFn: (s) => (s.lastHealthOk == null ? 2 : s.lastHealthOk ? 0 : 1), cell: ({ row: { original: s } }) => <HealthCell s={s} /> },
    {
      id: "links", header: t("admin:solutions.fields.deepLinks"), accessorFn: (s) => s.deepLinks.length,
      cell: ({ row: { original: s } }) => <span className="text-xs tabular text-muted-foreground">{s.deepLinks.length ? t("admin:solutions.linkCount", { count: s.deepLinks.length }) : "—"}</span>,
    },
    { id: "flag", header: t("admin:solutions.fields.flag"), accessorFn: (s) => s.flagKey ?? "", cell: ({ row: { original: s } }) => <FlagBadge s={s} /> },
    {
      id: "usedBy", header: t("admin:solutions.usedBy"), accessorFn: (s) => s.usedByWorkspaceKeys.length,
      cell: ({ row: { original: s } }) => s.usedByWorkspaceKeys.length ? (
        <span className="flex flex-wrap gap-1">{s.usedByWorkspaceKeys.slice(0, 3).map((k) => <Badge key={k} variant="outline" size="sm" className="font-mono">{k}</Badge>)}{s.usedByWorkspaceKeys.length > 3 ? <Badge variant="outline" size="sm">+{s.usedByWorkspaceKeys.length - 3}</Badge> : null}</span>
      ) : <span className="text-xs text-muted-foreground">{s.key === "assistant" || s.key === "admin" ? t("admin:solutions.everywhere") : "—"}</span>,
    },
    { id: "threads", header: t("admin:solutions.threadsMonth"), accessorFn: (s) => s.threadsMonth, cell: ({ row: { original: s } }) => <span className="tabular">{formatNumber(s.threadsMonth, locale)}</span> },
    {
      id: "actions", header: "", enableSorting: false,
      cell: ({ row: { original: s } }) => canWrite && s.probeUrl ? (
        <Tooltip content={t("admin:solutions.probe.one")}>
          <Button size="icon-sm" variant="ghost" aria-label={t("admin:solutions.probe.one")} loading={actions.probe.isPending && actions.probe.variables === s.key}
            onClick={(e) => { e.stopPropagation(); actions.probe.mutate(s.key, { onSuccess: (r) => (r.ok ? notifyDone(t("admin:solutions.probe.up", { name: s.name, ms: r.latencyMs })) : toast.error(t("admin:solutions.probe.down", { name: s.name }), { description: r.error ?? `HTTP ${r.statusCode}` })) }); }}>
            <RefreshCw />
          </Button>
        </Tooltip>
      ) : null,
    },
  ], [t, locale, canWrite, actions.probe]);

  return (
    <div>
      <PageHeader
        title={t("admin:solutions.title")}
        description={t("admin:solutions.subtitle")}
        actions={canWrite ? (<>
          <Button variant="outline" loading={actions.probeAll.isPending} onClick={probeAll}><HeartPulse /> {t("admin:solutions.probe.all")}</Button>
          <Button variant="gold" onClick={() => setCreating(true)}><Plus /> {t("admin:solutions.newSolution")}</Button>
        </>) : undefined}
      />

      <div className="mb-6 grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
        <StatCard label={t("admin:solutions.stats.surfaces")} value={all.filter((s) => s.enabled).length} unit={all.some((s) => !s.enabled) ? `/ ${all.length}` : undefined} icon={AppWindow} tone="gold" footer={t("admin:solutions.stats.surfacesHint")} />
        <StatCard label={t("admin:solutions.stats.healthy")} value={up} unit={probed.length ? `/ ${probed.length}` : undefined} icon={Activity} tone={probed.length && up < probed.length ? "camel" : "sea"} footer={t("admin:solutions.stats.healthyHint")} />
        <StatCard label={t("admin:solutions.stats.threads")} value={all.reduce((n, s) => n + s.threadsMonth, 0)} icon={MessagesSquare} tone="neutral" footer={t("admin:solutions.stats.threadsHint")} />
        <StatCard label={t("admin:solutions.stats.gated")} value={all.filter((s) => s.flag?.exists).length} icon={Flag} tone="neutral" footer={t("admin:solutions.stats.gatedHint")} />
      </div>

      <DataTable
        columns={columns}
        data={rows}
        loading={solutions.isLoading}
        pageSize={20}
        onRowClick={(s) => navigate(`/solutions/${encodeURIComponent(s.key)}`)}
        rowClassName={(s) => (!s.enabled ? "opacity-60" : undefined)}
        toolbar={(
          <div className="flex flex-wrap items-center gap-2">
            <div className="relative"><Search className="pointer-events-none absolute start-2.5 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" /><Input value={q} onChange={(e) => setQ(e.target.value)} placeholder={t("admin:solutions.searchPlaceholder")} className="h-9 w-64 ps-8" /></div>
            <NativeSelect value={kind} className="h-9 w-48" onChange={(e) => setKind(e.target.value)}>
              <option value="">{t("admin:solutions.fields.kind")}: {t("admin:common.all")}</option>
              {SOLUTION_KINDS.map((k) => <option key={k} value={k}>{t(`admin:solutions.kind.${k}`)}</option>)}
            </NativeSelect>
          </div>
        )}
        emptyState={<EmptyState icon={AppWindow} title={all.length ? t("admin:solutions.noMatch") : t("admin:solutions.emptyTitle")} />}
      />

      <CreateSolutionDialog open={creating} onOpenChange={setCreating} existing={all} onCreated={(key) => navigate(`/solutions/${encodeURIComponent(key)}`)} />
    </div>
  );
}

export function HealthCell({ s, showUrl = false }: { s: Solution; showUrl?: boolean }) {
  const { t } = useTranslation();
  const { locale } = useLocale();
  if (!s.probeUrl) return <span className="text-xs text-muted-foreground">{t("admin:solutions.health.notProbed")}</span>;
  if (s.lastHealthOk == null) return <span className="text-xs text-muted-foreground">{t("admin:solutions.health.unknown")}</span>;
  return (
    <span className="flex min-w-0 flex-col gap-0.5 text-xs">
      <span className="flex items-center gap-1.5 whitespace-nowrap">
        <span className={cn("size-2.5 shrink-0 rounded-full", s.lastHealthOk ? "bg-success-500" : "bg-danger-500 animate-pulse")} aria-hidden />
        <span className={cn("font-medium", s.lastHealthOk ? "text-success-700" : "text-danger-700")}>{s.lastHealthOk ? t("admin:solutions.health.up") : t("admin:solutions.health.down")}</span>
        {s.lastHealthOk && s.lastHealthLatencyMs != null ? <span className="tabular text-muted-foreground">{s.lastHealthLatencyMs} ms</span> : null}
      </span>
      {s.lastHealthAt ? <span className="whitespace-nowrap text-[11px] text-muted-foreground">{formatRelative(s.lastHealthAt, locale)}</span> : null}
      {showUrl ? <code className="truncate font-mono text-[11px] text-muted-foreground" dir="ltr">{s.probeUrl}</code> : null}
    </span>
  );
}

export function FlagBadge({ s }: { s: Solution }) {
  const { t } = useTranslation();
  if (!s.flagKey) return <span className="text-xs text-muted-foreground">—</span>;
  const f = s.flag;
  const label = !f?.exists ? t("admin:solutions.flag.missing") : !f.enabled ? t("admin:solutions.flag.off") : f.percentage < 100 ? t("admin:solutions.flag.partial", { pct: f.percentage }) : t("admin:solutions.flag.on");
  return (
    <Tooltip content={<span className="font-mono" dir="ltr">{s.flagKey}</span>}>
      <Badge size="sm" variant={!f?.exists ? "outline" : !f.enabled ? "danger" : f.percentage < 100 ? "warning" : "success"} className="gap-1"><Flag className="size-3" /> {label}</Badge>
    </Tooltip>
  );
}

function CreateSolutionDialog({ open, onOpenChange, existing, onCreated }: { open: boolean; onOpenChange: (o: boolean) => void; existing: Solution[]; onCreated: (key: string) => void }) {
  const { t } = useTranslation();
  const actions = useSolutionActions();
  const [name, setName] = useState("");
  const [nameAr, setNameAr] = useState("");
  const [key, setKey] = useState("");
  const [keyTouched, setKeyTouched] = useState(false);
  const [kind, setKind] = useState<SolutionKind>("WebApp");
  const [baseUrl, setBaseUrl] = useState("");
  const effectiveKey = keyTouched ? key : slugFromLabel(name);
  const keyError = !effectiveKey ? null : !/^[a-z][a-z0-9-]{1,63}$/.test(effectiveKey) ? t("admin:solutions.create.keyInvalid") : existing.some((s) => s.key === effectiveKey) ? t("admin:solutions.create.keyTaken") : null;
  const urlError = baseUrl && !/^https?:\/\/\S+$/i.test(baseUrl) ? t("admin:solutions.create.urlInvalid") : null;
  const needsUrl = kind !== "Channel";
  const ready = name.trim().length > 1 && effectiveKey && !keyError && !urlError && (!needsUrl || !!baseUrl);
  const reset = () => { setName(""); setNameAr(""); setKey(""); setKeyTouched(false); setKind("WebApp"); setBaseUrl(""); };

  const create = async () => {
    try {
      const s = await actions.create.mutateAsync({
        key: effectiveKey, name: name.trim(), nameAr: nameAr.trim() || null, description: null, descriptionAr: null, kind, icon: KIND_ICON[kind], baseUrl: baseUrl.trim(),
        deepLinks: [], requiredPermissions: [], healthUrl: null, flagKey: null, supportsHandoff: kind === "WebApp", adapterKey: null, settings: {}, enabled: true, sortOrder: 100,
      });
      notifySaved(s, t("admin:solutions.create.created"));
      onOpenChange(false); reset();
      onCreated(s.key);
    } catch { /* useSave reported it */ }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { onOpenChange(o); if (!o) reset(); }}>
      <DialogContent size="md">
        <DialogHeader><DialogTitle>{t("admin:solutions.newSolution")}</DialogTitle><DialogDescription>{t("admin:solutions.create.intro")}</DialogDescription></DialogHeader>
        <div className="grid gap-3 sm:grid-cols-2">
          <Field label={t("admin:common.name")} required><Input value={name} onChange={(e) => setName(e.target.value)} placeholder={t("admin:solutions.create.namePlaceholder")} autoFocus /></Field>
          <Field label={t("admin:common.nameAr")}><Input value={nameAr} dir="rtl" onChange={(e) => setNameAr(e.target.value)} /></Field>
          <Field label={t("admin:common.key")} hint={t("admin:solutions.fields.keyHint")} error={keyError ?? undefined}><Input value={effectiveKey} dir="ltr" className="font-mono" onChange={(e) => { setKeyTouched(true); setKey(e.target.value.toLowerCase()); }} /></Field>
          <Field label={t("admin:solutions.fields.kind")} hint={t(`admin:solutions.kindHint.${kind}`)}>
            <NativeSelect value={kind} onChange={(e) => setKind(e.target.value as SolutionKind)}>{SOLUTION_KINDS.map((k) => <option key={k} value={k}>{t(`admin:solutions.kind.${k}`)}</option>)}</NativeSelect>
          </Field>
          <Field label={t("admin:solutions.fields.baseUrl")} required={needsUrl} hint={t("admin:solutions.fields.baseUrlHint")} error={urlError ?? undefined} className="sm:col-span-2">
            <Input value={baseUrl} dir="ltr" className="font-mono" placeholder="https://eservices.moec.gov.ae" onChange={(e) => setBaseUrl(e.target.value.trim())} />
          </Field>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>{t("admin:common.cancel")}</Button>
          <Button variant="gold" disabled={!ready} loading={actions.create.isPending} onClick={create}><Plus /> {t("admin:solutions.create.createOpen")}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
